/**
 * <rs-cart-drawer>: Resilia "Your Bag" cart drawer.
 *
 * Renders from the AJAX cart (/cart.js), opens after add-to-cart and from any
 * [data-rs-cart-open] trigger, and keeps Ship's cart bubble in sync by
 * re-dispatching the theme's cart lines update event.
 */
(() => {
  if (customElements.get('rs-cart-drawer')) return;

  const CART_EVENT = 'shopify:cart:lines-update';
  const SOURCE = 'rs-cart-drawer';
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const escapeHtml = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  function formatMoney(cents, format) {
    const value = Number(cents) || 0;
    const pattern = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = format || '${{amount}}';

    const withDelimiters = (number, precision, thousands = ',', decimal = '.') => {
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? grouped + decimal + fraction : grouped;
    };

    const match = fmt.match(pattern);
    if (!match) return fmt;

    let amount;
    switch (match[1]) {
      case 'amount_no_decimals':
        amount = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = withDelimiters(value, 0, ' ');
        break;
      case 'amount_with_space_separator':
        amount = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        amount = withDelimiters(value, 2);
    }

    return fmt.replace(pattern, amount);
  }

  function deliveryDate(days) {
    const date = new Date();
    date.setDate(date.getDate() + (Number(days) || 0));
    const day = date.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return `${MONTHS[date.getMonth()]} ${day}${suffix}`;
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  }

  class RsCartDrawer extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector('[data-rs-cart-config]')?.textContent || '{}');
      } catch (error) {
        console.error('[rs-cart] Invalid config', error);
        this.config = {};
      }

      this.cart = this.config.cart || { items: [], item_count: 0, total_price: 0 };
      this.panel = this.querySelector('.rs-cart__panel');
      this.body = this.querySelector('[data-rs-cart-body]');
      this.icons = {};
      const iconTemplate = this.querySelector('template[data-rs-cart-icons]');
      iconTemplate?.content.querySelectorAll('svg[data-icon]').forEach((svg) => {
        this.icons[svg.dataset.icon] = svg.outerHTML;
      });

      this.render();

      this.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener(CART_EVENT, this.onCartEvent);
      document.addEventListener('openCart', () => this.open());
      document.addEventListener('rs:cart:open', () => this.open());
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener(CART_EVENT, this.onCartEvent);
    }

    onKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    };

    onDocumentClick = (event) => {
      const trigger = event.target instanceof Element ? event.target.closest('[data-rs-cart-open]') : null;
      if (!trigger) return;
      event.preventDefault();
      this.lastTrigger = trigger;
      this.open();
    };

    onClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest('[data-rs-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (target.closest('[data-rs-cart-clear]')) {
        event.preventDefault();
        this.request(this.config.routes.clear, {});
        return;
      }

      const change = target.closest('[data-rs-cart-change]');
      if (change) {
        event.preventDefault();
        this.request(this.config.routes.change, { id: change.dataset.key, quantity: Number(change.dataset.quantity) });
      }
    };

    onCartEvent = (event) => {
      if (event.rsSource === SOURCE) return;

      const refresh = () =>
        this.fetchCart().then(() => {
          if (event.action === 'add') this.open();
        });

      if (event.promise && typeof event.promise.then === 'function') {
        event.promise
          .then((result) => {
            if (result?.detail?.didError) return this.fetchCart();
            return refresh();
          })
          .catch(() => this.fetchCart());
      } else {
        refresh();
      }
    };

    async fetchCart() {
      try {
        const response = await fetch(`${this.config.routes.cartJs}?t=${Date.now()}`, { headers: { Accept: 'application/json' } });
        this.cart = await response.json();
        this.render();
      } catch (error) {
        console.error('[rs-cart] Could not load cart', error);
      }
      return this.cart;
    }

    async request(url, body) {
      this.classList.add('is-busy');
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        this.cart = data && Array.isArray(data.items) ? data : await this.fetchCart();
        this.render();
        this.notify();
      } catch (error) {
        console.error('[rs-cart] Cart update failed', error);
        await this.fetchCart();
      } finally {
        this.classList.remove('is-busy');
      }
    }

    /** Lets Ship's cart icon and product forms pick up changes made in this drawer. */
    notify() {
      const count = this.cart.item_count || 0;
      const event = new Event(CART_EVENT, { bubbles: true });
      Object.assign(event, {
        rsSource: SOURCE,
        action: 'change',
        context: SOURCE,
        lines: [],
        promise: Promise.resolve({
          cart: { totalQuantity: count },
          detail: { itemCount: count, items: this.cart.items, source: SOURCE },
        }),
      });
      document.dispatchEvent(event);
    }

    open() {
      this.fetchCart();
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.setAttribute('scroll-lock', '');
      requestAnimationFrame(() => this.panel?.focus({ preventScroll: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.removeAttribute('scroll-lock');
      this.lastTrigger?.focus?.({ preventScroll: true });
    }

    money(cents) {
      return escapeHtml(formatMoney(cents, this.config.moneyFormat));
    }

    title(item) {
      const prefix = this.config.stripPrefix || '';
      const title = item.product_title || item.title || '';
      return prefix && title.startsWith(prefix) ? title.slice(prefix.length) : title;
    }

    lineHtml(item) {
      const cfg = this.config;
      const badges = [];
      if (cfg.showVariantBadge && item.variant_title && !item.product_has_only_default_variant) {
        badges.push(item.variant_title);
      }
      if (item.selling_plan_allocation && cfg.subscriptionBadge) badges.push(cfg.subscriptionBadge);

      const options = [];
      if (!cfg.showVariantBadge && !item.product_has_only_default_variant && item.options_with_values) {
        item.options_with_values.forEach((option) => {
          if (!String(option.value).includes('Default Title')) options.push(option.value);
        });
      }
      if (item.selling_plan_allocation && !cfg.subscriptionBadge) options.push(item.selling_plan_allocation.selling_plan.name);
      Object.entries(item.properties || {}).forEach(([key, value]) => {
        if (key.startsWith('_') || value === '' || value == null) return;
        options.push(`${key}: ${value}`);
      });

      const compare = item.original_line_price > item.final_line_price
        ? `<s class="rs-cart__line-compare">${this.money(item.original_line_price)}</s>`
        : '';

      const quantity = cfg.showQuantity
        ? `<div class="rs-cart__qty-row">
            <div class="rs-cart__qty">
              <button type="button" aria-label="${escapeHtml(cfg.decreaseLabel)}" data-rs-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity - 1}">−</button>
              <span class="rs-cart__qty-value">${item.quantity}</span>
              <button type="button" aria-label="${escapeHtml(cfg.increaseLabel)}" data-rs-cart-change data-key="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}">+</button>
            </div>
            <button type="button" class="rs-cart__remove" data-rs-cart-change data-key="${escapeHtml(item.key)}" data-quantity="0">${escapeHtml(cfg.removeLabel)}</button>
          </div>`
        : '';

      const image = item.featured_image?.url || item.image;
      const title = escapeHtml(this.title(item));

      return `<li class="rs-cart__item">
        <div class="rs-cart__line">
          <div class="rs-cart__line-inner" data-line-key="${escapeHtml(item.key)}">
            <a class="rs-cart__line-image" href="${escapeHtml(item.url)}" tabindex="-1" aria-hidden="true">
              ${image ? `<img src="${escapeHtml(sizedImage(image, 200))}" alt="" width="100" height="100" loading="lazy">` : ''}
            </a>
            <div class="rs-cart__line-content">
              <div class="rs-cart__line-wrapper">
                ${badges.length ? `<div class="rs-cart__badges">${badges.map((badge) => `<span class="rs-cart__badge">${escapeHtml(badge)}</span>`).join('')}</div>` : ''}
                <a class="rs-cart__line-title" href="${escapeHtml(item.url)}">${title}</a>
                ${options.length ? `<ul class="rs-cart__options">${options.map((option) => `<li>${escapeHtml(option)}</li>`).join('')}</ul>` : ''}
                <div class="rs-cart__line-price-wrap">
                  ${compare}
                  <span class="rs-cart__line-price">${this.money(item.final_line_price)}</span>
                </div>
                ${quantity}
              </div>
            </div>
          </div>
        </div>
      </li>`;
    }

    render() {
      if (!this.body) return;
      const cart = this.cart || { items: [] };
      const cfg = this.config;

      if (!cart.items || cart.items.length === 0) {
        const empty = this.querySelector('template[data-rs-cart-empty]');
        this.body.innerHTML = empty ? empty.innerHTML : '';
        this.classList.add('is-empty');
        return;
      }

      this.classList.remove('is-empty');

      const subtotalCompare = cart.original_total_price > cart.total_price
        ? `<s class="rs-cart__subtotal-compare">${this.money(cart.original_total_price)}</s>`
        : '';

      const shipping = cfg.showShipping
        ? `<div class="rs-cart__shipping">${this.icons.truck || ''}<b>${escapeHtml(cfg.shippingBold)}</b> ${escapeHtml(cfg.shippingText)} ${escapeHtml(deliveryDate(cfg.deliveryDays))}</div>`
        : '';

      const guaranteeItems = (cfg.guarantee || []).filter(Boolean);
      const guaranteeIcons = [this.icons.refill, this.icons.clock];
      const guarantee = guaranteeItems.length
        ? `<div class="rs-cart__guarantee">${guaranteeItems
            .map((text, index) => `<div>${guaranteeIcons[index] || ''}<span class="rs-cart__guarantee-text">${escapeHtml(text)}</span></div>`)
            .join('<div class="rs-cart__divider">|</div>')}</div>`
        : '';

      this.body.innerHTML = `
        <div class="rs-cart__inner">
          <ul class="rs-cart__items">${cart.items.map((item) => this.lineHtml(item)).join('')}</ul>
          <div><button type="button" class="rs-cart__clear" data-rs-cart-clear>${escapeHtml(cfg.clearLabel)}</button></div>
        </div>
        <div class="rs-cart__bottom">
          <div class="rs-cart__subtotal">
            <div>${escapeHtml(cfg.subtotalLabel)}</div>
            <div class="rs-cart__subtotal-prices">${subtotalCompare}<div class="rs-cart__subtotal-price">${this.money(cart.total_price)}</div></div>
          </div>
          ${shipping}
          <div class="rs-cart__cta">
            <a href="${escapeHtml(cfg.routes.checkout)}" class="rs-cart__btn rs-cart__btn--checkout">${escapeHtml(cfg.checkoutLabel)} • ${this.money(cart.total_price)}</a>
          </div>
          ${guarantee}
        </div>`;
    }
  }

  customElements.define('rs-cart-drawer', RsCartDrawer);
})();
